import { TextTruncate } from "components";
import { Tooltip } from "components/common/tooltip";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import {
  formatUserName,
  isUserDeleted,
  type PerformanceFilter,
  isUserFromCurrentAccount as isUserFromCurrentAccountUtil,
} from "features/Library/Common/utils/performanceUtils";
import { GenericPerformanceList } from "features/Reporting/Content/shared/GenericPerformanceList";
import { type PeopleAcquisitionDetails } from "features/Reporting/types/people";
import { type ColumnOption } from "interfaces/columnOptions";
import { useMemo } from "react";
import { usePeopleAcquisitionDrilldownQuery } from "./queries";
import { MoboLink } from "components/MoboLink/MoboLink";
import RouteNames from "enums/routeNames";
import styles from "./poepleAcquisitionDrilldown.module.scss";
import { useReportingFormatWithTimeZone } from "hooks/useReportingFormatWithTimeZone";

export type Props = {
  dateFilter: PerformanceFilter;
  accountId: number;
  selectedCampaign: string;
  selectedMedium: string;
  selectedSource: string;
};

export const PeopleAcquisitionDrilldown = ({
  dateFilter,
  accountId,
  selectedCampaign,
  selectedMedium,
  selectedSource,
}: Props) => {
  const detailsQuery = usePeopleAcquisitionDrilldownQuery({
    dateFilter,
    selectedCampaign,
    selectedMedium,
    selectedSource,
  });
  const formatDateTime = useReportingFormatWithTimeZone();
  const columnOptions: ColumnOption<PeopleAcquisitionDetails>[] = useMemo(
    () => [
      {
        name: "NAME",
        width: 6,
        render(item) {
          const formattedNameDisplay = formatUserName(item.FirstName, item.LastName);
          const shouldShowLink = !isUserDeleted(item.FirstName, item.LastName);
          const isUserFromCurrentAccount = isUserFromCurrentAccountUtil(item.AccountId, accountId);
          const userName = (
            <Tooltip target={<TextTruncate>{formattedNameDisplay}</TextTruncate>} content={formattedNameDisplay} />
          );
          return (
            <div>
              {shouldShowLink ? (
                <MoboLink
                  accountId={isUserFromCurrentAccount ? undefined : item.AccountId}
                  target={isUserFromCurrentAccount ? "_self" : "_blank"}
                  to={`/${RouteNames.peopleUsers}/${item.UserId}`}
                  className={styles.linkButton}
                >
                  {userName}
                </MoboLink>
              ) : (
                userName
              )}
            </div>
          );
        },
      },
      {
        name: "DEPARTMENT",
        width: 5,
        render(item) {
          const department = item.Department ?? "-";
          return <Tooltip target={<TextTruncate>{department}</TextTruncate>} content={department} />;
        },
      },
      {
        name: "JOB TITLE",
        width: 5,
        render(item) {
          const title = item.JobTitle ?? "-";
          return <Tooltip target={<TextTruncate>{title}</TextTruncate>} content={title} />;
        },
      },
      {
        name: "DATE",
        width: 4,
        render(item) {
          const formattedDate = formatDateTime(item.ClickedAt);
          const datePart = formattedDate.split(" ")[0];
          return <Tooltip target={<TextTruncate>{datePart}</TextTruncate>} content={datePart} />;
        },
      },
      {
        name: "TIME",
        width: 4,
        render(item) {
          const formattedDate = formatDateTime(item.ClickedAt);
          const timePart = formattedDate.split(" ").slice(1).join(" ");
          return <Tooltip target={<TextTruncate>{timePart}</TextTruncate>} content={timePart} />;
        },
      },
    ],
    [accountId, formatDateTime],
  );

  return (
    <div className="people-acquisition-drilldown">
      <RequestStatusRenderer state={detailsQuery.status}>
        {detailsQuery.data && (
          <GenericPerformanceList
            title={selectedCampaign}
            rows={detailsQuery.data}
            columns={columnOptions}
            filter={dateFilter}
          />
        )}
      </RequestStatusRenderer>
    </div>
  );
};
